import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interest Categories (kept for compat) + new Categories API

/**
 * Normalize a flat category list so that any subcategory without its own icon
 * inherits the icon from its parent category. This ensures icons always display
 * everywhere — activity cards, org profile, filters, etc.
 */
function _inheritParentIcons(categories) {
  const parentIcon = {};
  categories.forEach(c => { if (!c.parent_id && c.icon) parentIcon[c.id] = c.icon; });
  return categories.map(c =>
    (c.parent_id && !c.icon && parentIcon[c.parent_id])
      ? { ...c, icon: parentIcon[c.parent_id] }
      : c
  );
}

export const getInterests   = () => api.get('/categories').then(r => ({ ...r, data: _inheritParentIcons(r.data) }));
export const getCategories  = (params) => api.get('/categories', { params });
export const getCategory    = (id)     => api.get(`/categories/${id}`);
export const createCategory = (data)   => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id)    => api.delete(`/categories/${id}`);

// Organizations (new canonical API)
export const getOrganizations = (params) => api.get('/organizations', { params });
export const getOrganization  = (idOrSlug) => api.get(`/organizations/${idOrSlug}`);
export const getOrgProducts   = (idOrSlug) => api.get(`/organizations/${idOrSlug}/products`);

// Changemakers (shim → /changemakers → /organizations internally)
export const getChangemakers      = (params) => api.get('/changemakers', { params });
export const getExploreExpiredRecommendations = (slug) => api.get(`/recommendations/explore-expired?activity_slug=${slug}`);
export const getExploreExpiredAnalytics       = (params) => api.get('/admin/analytics/explore-expired', { params });

export const getChangemaker       = (id)     => api.get(`/changemakers/${id}`);
export const getChangemakerBySlug = (slug)   => api.get(`/changemakers/${slug}`);

// Activities (public)
export const getActivities = (params) => api.get('/activities', { params });
export const getActivity   = (id)     => api.get(`/activities/${id}`);

// Paginated, server-side-scored activity feed
export const getActivitiesFeed = (params = {}) => {
  const {
    offset = 0, limit = 30,
    dates = [], interests = [],
    lat, lng, radiusKm,
    search, sessionId, sharedActivityId,
    showOnline,
  } = params;
  const p = new URLSearchParams();
  p.set('offset', offset);
  p.set('limit', limit);
  dates.forEach(d => p.append('dates', d));
  interests.forEach(i => p.append('interests', i));
  if (lat != null) p.set('lat', lat);
  if (lng != null) p.set('lng', lng);
  if (radiusKm != null) p.set('radius_km', radiusKm);
  if (search) p.set('search', search);
  if (sessionId) p.set('session_id', sessionId);
  if (sharedActivityId) p.set('shared_activity_id', sharedActivityId);
  if (showOnline != null) p.set('show_online', showOnline);
  return api.get(`/activities/feed?${p.toString()}`);
};

// Calendar date-dot API — returns { dates: ['YYYY-MM-DD', ...] }
export const getActivityCalendarDates = (fromDate, toDate) =>
  api.get(`/activities/calendar-dates?from_date=${fromDate}&to_date=${toDate}`);

// Registration
export const getMyRegistrations       = () => api.get('/registrations/mine');
export const getMyRegistration        = (activityId) => api.get(`/activities/${activityId}/my-registration`);
export const registerForActivity      = (activityId, data) => api.post(`/activities/${activityId}/register`, data);
export const registerAnonymous        = (activityId, data) => api.post(`/activities/${activityId}/register-anonymous`, data);
export const registerVerifyOTP        = (activityId, data) => api.post(`/activities/${activityId}/register-verify-otp`, data);
export const registerVerifyFirebase   = (activityId, data) => api.post(`/activities/${activityId}/register-verify-firebase`, data);

// Companies
export const getCompanies = () => api.get('/companies');
export const getCompany = (id) => api.get(`/companies/${id}`);
export const trackBuyClick = (companyId, userId) =>
  api.post(`/companies/${companyId}/track-click`, null, { params: { user_id: userId } });

// Reviews
export const getChangemakerReviews = (changemakerId, status = 'approved') =>
  api.get(`/reviews/changemaker/${changemakerId}`, { params: { status } });

// ---- Auth & user workflows (Milestone 4) ----
export const googleSession = (payload) => api.post('/auth/google-session', payload);
export const googleAuth = (payload) => api.post('/auth/google', payload);
export const phoneAuth = (payload) => api.post('/auth/phone', payload);
export const getMe = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');
export const updateProfile = (data) => api.put('/auth/me', data);
export const patchProfile  = (data) => api.put('/auth/me', data); // alias used by ProfilePromptModal
export const updateNotificationPrefs = (prefs) => api.patch('/auth/me/notifications', prefs);
export const getMyActivities = () => api.get('/auth/me/activities');
export const getMyJourneys   = () => api.get('/auth/me/journeys');
export const getMyReviews = () => api.get('/auth/me/reviews');
export const toggleActivityInterest = (activityId) =>
  api.post(`/auth/activities/${activityId}/interest`);
export const checkActivityInterest = (activityId) =>
  api.get(`/auth/activities/${activityId}/interest`);
export const getEngagement = (activityId) =>
  api.get(`/auth/activities/${activityId}/engagement`);
export const markParticipation = (activityId, participated) =>
  api.post(`/auth/activities/${activityId}/participate`, { participated });
export const qrCheckin = (activityId, body) =>
  api.post(`/activities/${activityId}/qr-checkin`, body);
export const getMyParticipations = () => api.get('/auth/me/activities');
export const getActivityParticipations = (activityId) =>
  api.get(`/activities/${activityId}/participations`);
export const updateParticipationStatus = (activityId, participationId, status, note) =>
  api.put(`/activities/${activityId}/participations/${participationId}/status`, { status, note });
export const createReview = (data) => api.post('/auth/reviews', data);
export const getMyReviewForChangemaker = (changemakerId) => api.get(`/auth/reviews/my/${changemakerId}`);

// ---- Admin (Milestone 5) ----
export const adminCheck = () => api.get('/admin/check');
export const getDashboard = () => api.get('/analytics/dashboard');
export const getFunnel = (params = {}) => api.get('/admin/analytics/funnel', { params });

export const uploadImage = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const uploadVideo = (file, onProgress) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/admin/upload/video', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000, // 3 min — large videos need time through the ingress
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
};

// Admin lists (all statuses)
export const adminListActivities = () => api.get('/admin/activities');
export const getActivityInterestReport = (activityId) => api.get(`/admin/activities/${activityId}/interest-report`);
export const adminGetActivityRegistrations = (activityId) => api.get(`/admin/activities/${activityId}/registrations`);
export const adminListChangemakers = () => { throw new Error('adminListChangemakers removed — use adminListOrganizations'); };
export const adminListCompanies = () => api.get('/admin/companies');

// Unified organisations admin
export const adminListOrganizations        = (params = {}) => api.get('/admin/organizations', { params });
export const adminCreateOrganization       = (data) => api.post('/admin/organizations', data);
export const adminUpdateOrganization       = (id, data) => api.put(`/admin/organizations/${id}`, data);
export const adminUpdateOrganizationStatus = (id, status) => api.put(`/admin/organizations/${id}/status`, { status });
export const adminDeleteOrganization       = (id) => api.delete(`/admin/organizations/${id}`);


// Activities CRUD
export const createActivity = (data) => api.post('/activities', data);
export const updateActivity = (id, data) => api.put(`/activities/${id}`, data);
export const deleteActivity = (id) => api.delete(`/activities/${id}`);
export const publishActivity = (id) => api.post(`/activities/${id}/publish`);
export const unpublishActivity = (id) => api.post(`/activities/${id}/unpublish`);

// Changemakers CRUD
export const createChangemaker = (data) => api.post('/changemakers', data);
export const createMinimalChangemaker = (data) => api.post('/changemakers/minimal', data);
export const updateChangemaker = (id, data) => api.put(`/changemakers/${id}`, data);
export const deleteChangemaker = (id) => api.delete(`/changemakers/${id}`);
export const publishChangemaker = (id) => api.post(`/changemakers/${id}/publish`);
export const unpublishChangemaker = (id) => api.post(`/changemakers/${id}/unpublish`);

// Companies CRUD
export const createCompany = (data) => api.post('/admin/companies', data);
export const updateCompany = (id, data) => api.put(`/admin/companies/${id}`, data);
export const deleteCompany = (id) => api.delete(`/admin/companies/${id}`);
export const publishCompany = (id) => api.post(`/admin/companies/${id}/publish`);
export const unpublishCompany = (id) => api.post(`/admin/companies/${id}/unpublish`);

// Company Products CRUD
export const adminAddProduct = (companyId, data) => api.post(`/admin/companies/${companyId}/products`, data);
export const adminUpdateProduct = (companyId, productId, data) => api.put(`/admin/companies/${companyId}/products/${productId}`, data);
export const adminDeleteProduct = (companyId, productId) => api.delete(`/admin/companies/${companyId}/products/${productId}`);

// Bulk upload
export const bulkUploadChangemakers = (formData) =>
  api.post('/admin/changemakers/bulk-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000, // 5 min for large batches with image downloads
  });
export const reprocessChangemakerImages = () =>
  api.post('/admin/changemakers/reprocess-images', {}, { timeout: 300000 });
export const createInterest = (data) => api.post('/interests', data);
export const deleteInterest = (id) => api.delete(`/interests/${id}`);

// Localities
export const getLocalities = () => api.get('/localities');
export const createLocality = (data) => api.post('/localities', data);
export const deleteLocality = (id) => api.delete(`/localities/${id}`);
export const updateLocality = (id, data) => api.patch(`/localities/${id}`, data);
export const getAdminUserScoringProfile = (userId) => api.get(`/admin/users/${userId}/scoring-profile`);

// Activity registration click tracking
export const trackRegistrationClick = (activityId, source = 'unknown') =>
  api.post(`/activities/${activityId}/track-registration`, { source });

// Reviews moderation
export const getPendingReviews = () => api.get('/reviews/pending');
export const moderateReview = (id, status, rejection_reason) =>
  api.put(`/reviews/${id}/moderate`, { status, rejection_reason });

// Activity feedback
export const submitActivityFeedback = (activityId, data) =>
  api.post(`/auth/activities/${activityId}/feedback`, data);
export const getMyActivityFeedback = (activityId) =>
  api.get(`/auth/activities/${activityId}/my-feedback`);
export const uploadUserImage = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/auth/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Public upload for registration form image fields (no auth required — works for anonymous users too)
export const uploadRegistrationImage = (file, activityId) => {
  const fd = new FormData();
  fd.append('file', file);
  if (activityId) fd.append('activity_id', activityId);
  return api.post('/registration/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const submitGeneralFeedback = (text) => api.post('/feedback', { text });

// Changemaker likes
export const toggleChangemakerLike = (changemakerId) => api.post(`/auth/changemakers/${changemakerId}/like`);
export const getLikedChangemakers = () => api.get('/auth/liked-changemakers');
export const getReferralCount = () => api.get('/auth/me/referral-count');

// Admin user management
export const adminListUsers = () => api.get('/admin/users');
export const setUserRole    = (userId, is_admin) => api.put(`/admin/users/${userId}/role`, { is_admin });
export const deleteAdminUser = (userId)          => api.delete(`/admin/users/${userId}`);
export const addAdminByEmail = (email) => api.post('/admin/admins', { email });
export const getAdminSignups = () => api.get('/admin/signups');
export const getAdminUserLogins = (userId) => api.get(`/admin/users/${userId}/logins`);
export const getEngagementAnalytics = (days = 30) => api.get('/admin/analytics/engagement', { params: { days } });

// Donation Places (public)
export const getDonationPlaces = () => api.get('/donation-places');
export const getDonationPlace  = (id) => api.get(`/donation-places/${id}`);

// Donation Places (admin CRUD)
export const adminCreateDonationPlace = (data) => api.post('/admin/donation-places', data);
export const adminUpdateDonationPlace = (id, data) => api.put(`/admin/donation-places/${id}`, data);
export const adminDeleteDonationPlace = (id) => api.delete(`/admin/donation-places/${id}`);

// Cutlery Banks (public)
export const getCutleryBanks = () => api.get('/cutlery-banks');
export const getCutleryBank  = (id) => api.get(`/cutlery-banks/${id}`);

// Cutlery Banks (admin CRUD)
export const adminListCutleryBanks   = () => api.get('/admin/cutlery-banks');
export const adminCreateCutleryBank  = (data) => api.post('/admin/cutlery-banks', data);
export const adminUpdateCutleryBank  = (id, data) => api.put(`/admin/cutlery-banks/${id}`, data);
export const adminDeleteCutleryBank  = (id) => api.delete(`/admin/cutlery-banks/${id}`);

// ─────────────────────────────────────────────────────────────────────────────
// Changemaker Auth — REMOVED (legacy CM auth system deleted in Phase 4)
// These functions are kept as no-op stubs to avoid import errors during cleanup.
// ─────────────────────────────────────────────────────────────────────────────
export const checkChangemakerToken         = () => Promise.reject(new Error('Legacy CM auth removed'));
export const sendChangemakerOTP            = () => Promise.reject(new Error('Legacy CM auth removed'));
export const verifyChangemakerOTP          = () => Promise.reject(new Error('Legacy CM auth removed'));
export const changemakerGoogleAuth         = () => Promise.reject(new Error('Legacy CM auth removed'));
export const changemakerPhoneAuth          = () => Promise.reject(new Error('Legacy CM auth removed'));
export const changemakerGoogleAccessAuth   = () => Promise.reject(new Error('Legacy CM auth removed'));
export const changemakerLoginSendOTP       = () => Promise.reject(new Error('Legacy CM auth removed'));
export const changemakerLoginVerifyOTP     = () => Promise.reject(new Error('Legacy CM auth removed'));
export const changemakerLoginPhone         = () => Promise.reject(new Error('Legacy CM auth removed'));
// getChangemakerMe kept as alias to the new membership-based endpoint
export const getChangemakerMe              = () => api.get('/organizations/me');
// changemakerLogout is now a no-op — logout happens via AuthContext.logout()
export const changemakerLogout             = () => Promise.resolve();

// Phase 3: Organization membership + identity
export const getMyMemberships       = () => api.get('/auth/memberships');
export const claimOrganization      = (orgId, signup_token) => api.post(`/organizations/${orgId}/claim`, { signup_token });
export const linkAuthIdentity       = (provider, provider_uid) => api.post('/auth/identity/link', { provider, provider_uid });
export const confirmIdentityMerge   = (merge_token) => api.post('/auth/identity/merge/confirm', { merge_token });

// Phase 4: Org invite
export const getOrgInviteInfo = (orgSlug, token) => api.get(`/org-invite/${orgSlug}/${token}`);
export const acceptOrgInvite  = (orgSlug, token) => api.post(`/org-invite/${orgSlug}/${token}/accept`);

// Phase 5: Services — public
export const getServiceTypes        = () => api.get('/service-types');
export const getServiceOfferings    = (params = {}) => api.get('/service-offerings', { params });
export const getServiceOffering     = (id) => api.get(`/service-offerings/${id}`);
export const getOfferingLocations   = (offeringId) => api.get(`/service-offerings/${offeringId}/locations`);
export const getServiceLocations    = (params = {}) => api.get('/service-locations', { params });
export const getEnrichedServiceLocations = (params = {}) => api.get('/service-locations/enriched', { params });
export const getEnrichedServiceLocation  = (slugOrId) => api.get(`/service-locations/${slugOrId}/enriched`);
export const getOfferingsByProvider      = (providerId, serviceTypeSlugs = '') =>
  api.get(`/service-offerings/by-provider/${providerId}`, { params: { service_type_slugs: serviceTypeSlugs } });

// Phase 5: Services — admin types
export const adminListServiceTypes        = () => api.get('/admin/service-types');
export const adminCreateServiceType       = (data) => api.post('/service-types', data);
export const adminUpdateServiceType       = (slug, data) => api.put(`/service-types/${slug}`, data);
export const adminUpdateServiceTypeStatus = (slug, status) => api.put(`/admin/service-types/${slug}/status`, { status });
export const adminDeleteServiceType       = (slug) => api.delete(`/admin/service-types/${slug}`);
export const adminUploadServiceTypeImage  = (slug, file) => {
  const fd = new FormData(); fd.append('file', file);
  return api.post(`/admin/service-types/${slug}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Phase 5: Services — admin offerings
export const adminListServiceOfferings        = (params = {}) => api.get('/admin/service-offerings', { params });
export const adminCreateServiceOffering       = (data) => api.post('/service-offerings', data);
export const adminUpdateServiceOffering       = (id, data) => api.put(`/service-offerings/${id}`, data);
export const adminUpdateServiceOfferingStatus = (id, status) => api.put(`/admin/service-offerings/${id}/status`, { status });
export const adminDeleteServiceOffering       = (id) => api.delete(`/service-offerings/${id}`);

// Phase 5: Services — admin locations
export const adminListServiceLocations        = (params = {}) => api.get('/admin/service-locations', { params });
export const adminCreateServiceLocation       = (data) => api.post('/service-locations', data);
export const adminUpdateServiceLocation       = (id, data) => api.put(`/service-locations/${id}`, data);
export const adminUpdateServiceLocationStatus = (id, status) => api.put(`/admin/service-locations/${id}/status`, { status });
export const adminDeleteServiceLocation       = (id) => api.delete(`/service-locations/${id}`);
export const adminResolveServiceMapsUrl       = (url) => api.post('/service-locations/resolve-maps-url', { url });

// Org Team Members (new unified auth routes)
export const getChangemakerMembers = () => api.get('/organizations/me/members');
export const addChangemakerMember  = (data) => api.post('/organizations/me/members', data);

// Org Profile (new unified auth routes)
export const updateChangemakerProfile = (data) => api.put('/organizations/me/profile', data);

// Org Activities (new unified auth routes)
export const getChangemakerOwnActivities  = ()   => api.get('/organizations/me/activities');
export const publishChangemakerActivity   = (id) => api.post(`/organizations/me/activities/${id}/publish`);
export const unpublishChangemakerActivity = (id) => api.post(`/organizations/me/activities/${id}/unpublish`);
export const createChangemakerActivity    = (data) => api.post('/organizations/me/activities', data);
export const updateChangemakerActivity    = (id, data) => api.put(`/organizations/me/activities/${id}`, data);
export const cancelChangemakerActivity    = (id) => api.delete(`/organizations/me/activities/${id}`);
export const permanentDeleteChangemakerActivity = (id) => api.delete(`/organizations/me/activities/${id}/permanent`);

// Activity Collaborators
export const getActivityCollaborators     = (activityId) => api.get(`/organizations/me/activities/${activityId}/collaborators`);
export const addActivityCollaborator      = (activityId, data) => api.post(`/organizations/me/activities/${activityId}/collaborators`, data);
export const removeActivityCollaborator   = (activityId, collabId) => api.delete(`/organizations/me/activities/${activityId}/collaborators/${collabId}`);
export const sendCollabEmails             = (activityId, data) => api.post(`/organizations/me/activities/${activityId}/collaborators/send-emails`, data);
export const searchCollabOrgs             = (q = '') => api.get(`/organizations/me/collaborator-org-search`, { params: { q } });

export const uploadActivityMedia = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/organizations/me/upload-media', fd, {
    headers: { 'Content-Type': undefined },
  }).then(r => r.data.url);
};

export const extractActivityFromPoster = (file, descriptionText) => {
  const fd = new FormData();
  if (file) fd.append('file', file);
  if (descriptionText) fd.append('description_text', descriptionText);
  return api.post('/organizations/me/extract-activity', fd, {
    headers: { 'Content-Type': undefined },
  }).then(r => r.data);
};

// Admin: Org invite links
export const adminGetOrgInviteLink        = (orgId) => api.get(`/admin/organizations/${orgId}/invite-link`);
export const adminRegenerateOrgInviteLink = (orgId) => api.post(`/admin/organizations/${orgId}/invite-link/regenerate`);
export const adminSendOrgInviteEmail      = (orgId, emails) => api.post(`/admin/organizations/${orgId}/send-invite-email`, { emails });
export const adminGetOrgMembers           = (orgId) => api.get(`/admin/organizations/${orgId}/members`);
export const adminRemoveOrgMember         = (orgId, membershipId) => api.delete(`/admin/organizations/${orgId}/members/${membershipId}`);
export const adminGetOrgTeamInvites       = (orgId) => api.get(`/admin/organizations/${orgId}/team-invites`);
export const getOrgTeamInvite             = (token) => api.get(`/org-team-invite/${token}`);
export const acceptOrgTeamInvite          = (token) => api.post(`/org-team-invite/${token}/accept`);
export const adminGetOrgClaimedProfile    = (orgId) => api.get(`/admin/organizations/${orgId}/claimed-profile`);
export const adminUnclaimOrg              = (orgId) => api.put(`/admin/organizations/${orgId}/unclaim`);
export const adminExpirePendingInviteLinks = () => api.post('/admin/migrations/expire-pending-invite-links');

// Aliases (all point to the same correct endpoints now)
export const adminGetSignupLink           = adminGetOrgInviteLink;
export const adminRegenerateSignupLink    = adminRegenerateOrgInviteLink;
export const adminGetOrgSignupLink        = adminGetOrgInviteLink;
export const adminRegenerateOrgSignupLink = adminRegenerateOrgInviteLink;
export const adminGetClaimedProfile       = adminGetOrgClaimedProfile;

// Admin: User changemaker toggle
export const toggleUserChangemaker = (userId, isChangemaker) => api.put(`/admin/users/${userId}/changemaker`, { is_changemaker: isChangemaker });

// Admin: SEO Tags management
export const adminListSEOStatic         = () => api.get('/admin/seo/pages/static');
export const adminListSEOActivities     = (params = {}) => api.get('/admin/seo/pages/activities', { params });
export const adminListSEOOrganizations  = () => api.get('/admin/seo/pages/organizations');
export const adminListSEODonateLocations = () => api.get('/admin/seo/pages/donate-locations');
export const adminSaveSEOOverride       = (data) => api.put('/admin/seo/override', data);
export const adminDeleteSEOOverride     = (pageType, pageId) =>
  api.delete(`/admin/seo/override/${encodeURIComponent(pageType)}/${encodeURIComponent(pageId)}`);

// Admin: Activities Report
export const adminGetActivitiesReport = (params = {}) => api.get('/admin/activities-report', { params });
export const adminGetRetentionCellUsers = (params = {}) => api.get('/admin/analytics/retention/cell-users', { params });
export const adminGetActivityCollaborators = (activityId) => api.get(`/admin/activities/${activityId}/collaborators`);
export const adminAddActivityCollaborator = (activityId, data) => api.post(`/admin/activities/${activityId}/collaborators`, data);
export const adminRemoveActivityCollaborator = (activityId, collabId) => api.delete(`/admin/activities/${activityId}/collaborators/${collabId}`);
export const adminGetRetention = (params = {}) => api.get('/admin/analytics/retention', { params });
export const adminGetActivityEngagement = (params = {}) => api.get('/admin/activity-engagement', { params });
export const adminGetActivityEngagementDetail = (activityId) => api.get(`/admin/activity-engagement/${activityId}`);
export const adminGetActivityEngagementDateChart = (params = {}) => api.get('/admin/activity-engagement/date-chart', { params });
export const adminGetActivityEngagementDateDetail = (params = {}) => api.get('/admin/activity-engagement/date-detail', { params });
export const adminBackfillOtherOrganisers = () => api.post('/admin/backfill-other-organisers');

// Admin: Share Links (WhatsApp tracking)
export const adminCreateShareLink = (data) => api.post('/admin/share-links', data);
export const adminGetShareLinks = () => api.get('/admin/share-links');
export const adminUpdateShareLink = (code, data) => api.put(`/admin/share-links/${code}`, data);
export const adminDeleteShareLink = (code) => api.delete(`/admin/share-links/${code}`);

// Admin: Generic media upload (works without org membership)
export const adminUploadMedia = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/admin/upload-media', fd, {
    headers: { 'Content-Type': undefined },
  }).then(r => r.data.url);
};

// Admin: Upload a PDF and receive up to 5 page-image URLs
export const adminUploadPdfPages = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/admin/upload-pdf-pages', fd, {
    headers: { 'Content-Type': undefined },
    timeout: 60000,
  }).then(r => r.data);
};

// Email OTP Authentication
export const sendEmailOtp = (payload) => api.post('/auth/email/send-otp', payload);
export const verifyEmailOtp = (payload) => api.post('/auth/email/verify-otp', payload);

// Org: Activity Registrations (admin view)
export const getOrgActivityRegistrations = (activityId) => api.get(`/organizations/me/activities/${activityId}/registrations`);

// Org: Onboarding Tutorial tracking
export const trackTutorialSession = (data) => api.post('/organizations/me/tutorial/track', data);

// Activity Updates (org)
export const getOrgActivityUpdates       = (activityId) => api.get(`/organizations/me/activities/${activityId}/updates`);
export const createOrgActivityUpdate     = (activityId, data) => api.post(`/organizations/me/activities/${activityId}/updates`, data);
export const updateOrgActivityUpdate     = (activityId, updateId, data) => api.put(`/organizations/me/activities/${activityId}/updates/${updateId}`, data);
export const deleteOrgActivityUpdate     = (activityId, updateId) => api.delete(`/organizations/me/activities/${activityId}/updates/${updateId}`);
export const sendOrgActivityUpdateEmail  = (activityId, updateId, body = {}) => api.post(`/organizations/me/activities/${activityId}/updates/${updateId}/send-email`, body);
export const getOrgUpdateEmailPreview    = (activityId, updateId) => api.get(`/organizations/me/activities/${activityId}/updates/${updateId}/email-preview`);

// Activity Updates (public)
export const getActivityUpdates = (activityId) => api.get(`/activities/${activityId}/updates`);


// Activity Updates (admin)
export const adminGetActivityUpdates      = (activityId) => api.get(`/admin/activities/${activityId}/updates`);
export const adminCreateActivityUpdate    = (activityId, data) => api.post(`/admin/activities/${activityId}/updates`, data);
export const adminUpdateActivityUpdate    = (activityId, updateId, data) => api.put(`/admin/activities/${activityId}/updates/${updateId}`, data);
export const adminDeleteActivityUpdate    = (activityId, updateId) => api.delete(`/admin/activities/${activityId}/updates/${updateId}`);
export const adminSendActivityUpdateEmail = (activityId, updateId) => api.post(`/admin/activities/${activityId}/updates/${updateId}/send-email`);

// Activity Comments (public read, auth-gated write)
export const getActivityComments   = (activityId) => api.get(`/activities/${activityId}/comments`);
export const postActivityComment   = (activityId, data) => api.post(`/activities/${activityId}/comments`, data);
export const deleteActivityComment = (activityId, commentId) => api.delete(`/activities/${activityId}/comments/${commentId}`);
export const getMyActivityInvites  = (activityId) => api.get(`/activities/${activityId}/my-invites`);

// Activity Journey progress
export const getJourneyProgress  = (activityId) => api.get(`/activities/${activityId}/journey-progress`);
export const markInspireShared       = (activityId) => api.post(`/activities/${activityId}/inspire-shared`);
export const generateInspireImage    = (activityId) => api.post(`/activities/${activityId}/generate-inspire-image`);
export const getInviteCode       = (activityId) => api.get(`/activities/${activityId}/invite-code`);
export const resolveInviteCode   = (code)       => api.get(`/i/${code}`);

// Participant Verification (organiser)
export const getActivityQRCode        = (activityId) => api.get(`/activities/${activityId}/qr-code`);
export const regenerateActivityQRCode = (activityId) => api.post(`/activities/${activityId}/qr-code/regenerate`);
export const getActivityParticipants  = (activityId) => api.get(`/activities/${activityId}/participants`);
export const verifyParticipant        = (activityId, userId, verified) => api.post(`/activities/${activityId}/participants/${userId}/verify`, { verified });

// Inspire card — event photo upload (org user: date-gated; admin: open)
export const uploadEventPhoto = (activityId, file) => {
  const fd = new FormData(); fd.append('file', file);
  return api.post(`/activities/${activityId}/event-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const adminUploadEventPhoto = (activityId, file) => {
  const fd = new FormData(); fd.append('file', file);
  return api.post(`/admin/activities/${activityId}/event-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Per-participant certificate upload (org user)
export const uploadParticipantCertificate = (activityId, userId, file) => {
  const fd = new FormData(); fd.append('file', file);
  return api.post(`/activities/${activityId}/participants/${userId}/certificate`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Google Forms Sync
export const getGoogleFormsAuthUrl    = (activityId) => api.get(`/google-forms/auth-url?activity_id=${activityId}`);
export const confirmGoogleFormsSync   = (activityId, tempToken, fieldMapping) =>
  api.post(`/google-forms/${activityId}/confirm-sync`, { temp_token: tempToken, field_mapping: fieldMapping });
export const selectGoogleFormsForm    = (activityId, tempToken, formId) =>
  api.post(`/google-forms/${activityId}/select-form`, { temp_token: tempToken, form_id: formId });
export const turnOffGoogleFormsSync   = (activityId) => api.post(`/google-forms/${activityId}/turn-off`);
export const getGoogleFormsSyncStatus = (activityId) => api.get(`/google-forms/${activityId}/status`);
export const triggerGoogleFormsSync   = (activityId) => api.post(`/google-forms/${activityId}/trigger-sync`);

// Org claim requests
export const submitOrgClaim         = (orgId, data) => api.post(`/orgs/${orgId}/claim`, data);
export const adminListOrgClaims     = (status)      => api.get('/admin/org-claim-requests', { params: status ? { status } : {} });
export const adminGetOrgClaim       = (id)          => api.get(`/admin/org-claim-requests/${id}`);
export const adminAcceptOrgClaim    = (id)          => api.post(`/admin/org-claim-requests/${id}/accept`);
export const adminRejectOrgClaim    = (id, data)    => api.post(`/admin/org-claim-requests/${id}/reject`, data);

export default api;
